import { MutableRefObject } from 'react'
import { ClientRect } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { Transform } from '@dnd-kit/utilities'

import { FlattenedItem, TreeItem, TreeItems } from '@/shared/builder'

export type SensorContext = MutableRefObject<{
  items: FlattenedItem[]
  offset: number
}>

function getMaxDepth({ previousItem }: { previousItem: FlattenedItem }) {
  if (previousItem) {
    return previousItem.nested ? previousItem.depth + 1 : previousItem.depth
  }

  return 0
}

function getMinDepth({ nextItem }: { nextItem: FlattenedItem }) {
  if (nextItem) {
    return nextItem.depth
  }

  return 0
}

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth)
}

export function getProjection(
  items: FlattenedItem[],
  activeId: string,
  overId: string,
  dragOffset: number,
  indentationWidth: number
) {
  let _overId = overId

  // handle placeholder
  const isPlaceholder = overId.toString().startsWith('placeholder-')
  if (isPlaceholder) {
    _overId = overId.toString().replace('placeholder-', '')
  }
  const overItemIndex = items.findIndex(({ id }) => id === _overId)
  const activeItemIndex = items.findIndex(({ id }) => id === activeId)
  const activeItem = items[activeItemIndex]
  const newItems = arrayMove(items, activeItemIndex, overItemIndex)
  const previousItem = newItems[overItemIndex - 1]
  const nextItem = newItems[overItemIndex + 1]
  const dragDepth = getDragDepth(dragOffset, indentationWidth)
  const projectedDepth = activeItem.depth + dragDepth
  const maxDepth = getMaxDepth({ previousItem })
  const minDepth = getMinDepth({ nextItem })
  let depth = projectedDepth

  if (projectedDepth >= maxDepth) {
    depth = maxDepth
  } else if (projectedDepth < minDepth) {
    depth = minDepth
  }

  const { parentId, parentTitle, parentType } = getParent()

  return {
    depth,
    maxDepth,
    minDepth,
    parentId: parentId ?? null,
    parentTitle: parentTitle ?? null,
    parentType: parentType ?? null,
  }

  function getParent() {
    if (depth === 0 || !previousItem) {
      return { parentId: null, parentTitle: null, parentType: null }
    }

    if (depth === previousItem.depth) {
      return {
        parentId: previousItem.parentId,
        parentTitle: previousItem.parentTitle,
        parentType: previousItem.parentType,
      }
    }

    if (depth > previousItem.depth) {
      if (previousItem.nested) {
        return {
          parentId: previousItem.id,
          parentTitle: previousItem.title,
          parentType: previousItem.compId,
        }
      } else {
        return { parentId: null, parentTitle: null, parentType: null }
      }
    }

    const { parentId, parentTitle, parentType } = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth) || {
      parentId: null,
      parentTitle: null,
      parentType: null,
    }

    return { parentId, parentTitle, parentType }
  }
}

export function removeChildrenOf(items: FlattenedItem[], ids: string[]) {
  const excludeParentIds = [...ids]

  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.children.length) {
        excludeParentIds.push(item.id)
      }
      return false
    }

    return true
  })
}

export function findItemDeep(items: TreeItems, itemId: string): TreeItem | undefined {
  for (const item of items) {
    const { id, children } = item

    if (id === itemId) {
      return item
    }

    if (children.length) {
      const child = findItemDeep(children, itemId)

      if (child) {
        return child
      }
    }
  }

  return undefined
}

function countChildren(items: TreeItem[], count = 0): number {
  return items.reduce((acc, { children }) => {
    if (children.length) {
      return countChildren(children, acc + 1)
    }

    return acc + 1
  }, count)
}

export function getChildCount(items: TreeItems, id: string) {
  const item = findItemDeep(items, id)
  return item ? countChildren(item.children) : 0
}

export function setProperty<T extends keyof TreeItem>(
  items: TreeItems,
  id: string,
  property: T,
  setter: (value: TreeItem[T]) => TreeItem[T]
) {
  for (const item of items) {
    if (!item) {
      continue
    }

    if (item.id === id) {
      item[property] = setter(item[property])
      continue
    }

    if (item.children.length) {
      item.children = setProperty(item.children, id, property, setter)
    }
  }

  return [...items]
}

export function findItem(items: TreeItem[], itemId: string) {
  return items.find(({ id }) => id === itemId)
}

/**
 * Utilities to split global and page elements from a flattened items array
 */
export function splitGlobalAndPageElements(
  flattenedItems: FlattenedItem[],
  _flattenedItemsMap?: Map<string, FlattenedItem>
) {
  const globalElements: FlattenedItem[] = []
  const pageElements: FlattenedItem[] = []

  // Always rebuild the map from the current flattened items to ensure consistency
  // This is important because the flattened items may have been updated (e.g., after drag)
  // while the map parameter might contain stale data
  const itemsMap = new Map<string, FlattenedItem>()
  for (const item of flattenedItems) {
    itemsMap.set(item.id, item)
  }

  // Helper function to check if an item or any of its ancestors is global
  const isGlobalOrHasGlobalAncestor = (item: FlattenedItem): boolean => {
    // Check if the item itself is global
    if (item.groupId === 'globals') {
      return true
    }

    // Check if any ancestor is global by walking up the parent chain
    let currentParentId = item.parentId
    while (currentParentId) {
      const parent = itemsMap.get(currentParentId)
      if (!parent) break
      
      if (parent.groupId === 'globals') {
        return true
      }
      
      currentParentId = parent.parentId
    }

    return false
  }

  // Iterate through all items and classify them
  for (const item of flattenedItems) {
    if (isGlobalOrHasGlobalAncestor(item)) {
      globalElements.push(item)
    } else {
      pageElements.push(item)
    }
  }

  return [globalElements, pageElements] as const
}

export function buildTree(flattenedItems: FlattenedItem[]): TreeItems {
  const root: TreeItem = { id: 'root', children: [] } as unknown as TreeItem
  const nodes: Record<string, TreeItem> = { [root.id]: root }
  const items = flattenedItems.map((item) => ({ ...item, children: [] }))

  for (const item of items) {
    const { id, children } = item
    const parentId = item.parentId ?? root.id
    const parent = nodes[parentId] ?? findItem(items, parentId)

    nodes[id] = { id, children } as unknown as TreeItem
    parent.children.push(item)
  }

  return root.children
}

export function restrictToBoundingRect(transform: Transform, rect: ClientRect, boundingRect: ClientRect): Transform {
  const value = {
    ...transform,
  }

  if (rect.top + transform.y <= boundingRect.top) {
    value.y = boundingRect.top - rect.top
  } else if (rect.bottom + transform.y >= boundingRect.top + boundingRect.height) {
    value.y = boundingRect.top + boundingRect.height - rect.bottom
  }

  if (rect.left + transform.x <= boundingRect.left) {
    value.x = boundingRect.left - rect.left
  } else if (rect.right + transform.x >= boundingRect.left + boundingRect.width) {
    value.x = boundingRect.left + boundingRect.width - rect.right
  }

  return value
}

// export function filterParentDeep(
//   items: TreeItems,
//   parentId: string | null,
//   filter: (item: TreeItem) => boolean
// ): boolean {
//   let isMatched = false
//   for (const item of items) {
//     if (item.id === parentId) {
//       if (filter(item)) {
//         isMatched = true
//       }
//       if (item.parentId) {
//         isMatched = filterParentDeep(items, item.parentId, filter)
//       }
//     }
//   }
//   return isMatched
// }

export function filterParentDeep(
  itemsMap: Map<string, FlattenedItem>,
  parentId: string | null,
  filter: (item: FlattenedItem) => boolean
): boolean {
  if (!parentId) return false

  const item = itemsMap.get(parentId)
  if (!item) return false

  if (filter(item)) {
    return true
  } else if (item.parentId) {
    return filterParentDeep(itemsMap, item.parentId, filter)
  }

  return false
}

export function filterParentDeepAlt(
  items: FlattenedItem[],
  parentId: string | null,
  filter: (item: FlattenedItem) => boolean
): FlattenedItem | undefined {
  if (!parentId) return

  const item = items.find((i) => i.id === parentId)
  if (!item) return

  if (filter(item)) {
    return item
  } else if (item.parentId) {
    return filterParentDeepAlt(items, item.parentId, filter)
  }
}
